import React from 'react';
import { Button, Grid, Stack, Typography } from '@mui/material';
import PriceChangeOutlinedIcon from '@mui/icons-material/PriceChangeOutlined';
import { formatPrice } from '@retail/products/utils';
import ArrowRightAltOutlinedIcon from '@mui/icons-material/ArrowRightAltOutlined';
import { useAppTFunction } from '@retail/app/i18n';
import { useTranslation } from 'react-i18next';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { PriceListSchema } from './priceListSchema';
import { PriceInput } from '../PriceInput';

interface Props {
  submitSimulation: () => void;
  isLoadingRoundingRuleSetSimulation: boolean;
  simulationResultPrices?: any[];
}

export const PriceRoundingSimulations: React.FC<Props> = ({
  submitSimulation,
  isLoadingRoundingRuleSetSimulation,
  simulationResultPrices,
}) => {
  const t = useAppTFunction();
  const { i18n } = useTranslation();

  const { control, watch } = useFormContext<PriceListSchema>();
  const { fields } = useFieldArray({ control, name: 'prices' });

  return (
    <Stack spacing={1} alignItems="start" flexGrow={1}>
      <Button
        variant="text"
        startIcon={<PriceChangeOutlinedIcon sx={{ width: 32, height: 32 }} />}
        onClick={submitSimulation}
        loading={isLoadingRoundingRuleSetSimulation}
      >
        {t('rounding.simulation.title')}
      </Button>
      <Grid container spacing={1} width="100%">
        <Grid size={1}>
          <Stack
            spacing={1}
            alignItems="start"
            justifyContent="space-between"
            width="max-content"
            height="100%"
            py={0.5}
          >
            <Typography variant="body1">{t('rounding.simulation.in')}</Typography>
            <Typography variant="body1">{t('rounding.simulation.out')}</Typography>
          </Stack>
        </Grid>
        {fields.map((_, index) => {
          const price = watch(`prices.${index}.price`);
          const outputPrice = simulationResultPrices?.find(
            (p) => p.inputPrice === Number(price?.toString().replace(',', '.'))
          )?.outputPrice;

          return (
            <Grid size={2} gap={1} key={index}>
              <Stack spacing={1} alignItems="center" width="max-content">
                <PriceInput
                  name={`prices.${index}.price`}
                  control={control}
                  size="xs"
                  sx={{ width: '120px' }}
                />
                <ArrowRightAltOutlinedIcon sx={{ transform: 'rotate(90deg)' }} />
                {outputPrice ? (
                  <span>
                    {t('products.prices.price', { price: formatPrice(outputPrice, i18n.language) })}
                  </span>
                ) : (
                  '-'
                )}
              </Stack>
            </Grid>
          );
        })}
      </Grid>
    </Stack>
  );
};
